import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getCourse, enrollCourse, paymentCatalog, createCheckout } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { ArrowRight, Lock, BookOpen, Clock, Check, Loader2, ShoppingCart } from "lucide-react";

const CourseDetail = () => {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [catalog, setCatalog] = useState({});
  const [enrolling, setEnrolling] = useState(false);
  const [err, setErr] = useState("");
  const [notFound, setNotFound] = useState(false);

  // V2-Kurse (Slugs mit "-v2") haben einen dedizierten Reader unter
  // /academy/v2/:courseSlug — direkt dorthin weiterleiten, damit der
  // Preview-Klick aus dem Admin auf tatsächlich lesbare Lektionen führt
  // (statt am v1-CourseDetail-API mit „Lade…" zu hängen).
  useEffect(() => {
    if (slug && slug.endsWith("-v2")) {
      nav(`/academy/v2/${slug}`, { replace: true });
    }
  }, [slug, nav]);

  useEffect(() => {
    if (slug && slug.endsWith("-v2")) return; // wird oben weitergeleitet
    getCourse(slug)
      .then((c) => { setCourse(c); setNotFound(!c); })
      .catch(() => setNotFound(true));
    paymentCatalog().then(setCatalog);
  }, [slug]);

  // Map slug -> product_key in catalog
  const productKey = useMemo(() => {
    const entries = Object.entries(catalog).find(([_, v]) => v.course_slug === slug);
    return entries ? entries[0] : null;
  }, [catalog, slug]);

  const startEnroll = async () => {
    if (!user) { nav(`/auth/login?redirect=/academy/${slug}`); return; }
    setErr(""); setEnrolling(true);
    try {
      if (!course?.price_chf || course.price_chf === 0) {
        await enrollCourse(slug);
        nav(`/academy/${slug}/learn`);
        return;
      }
      // Paid: start checkout
      if (!productKey) throw new Error("Kein Produkt im Katalog");
      const r = await createCheckout(productKey);
      window.location.href = r.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Fehler");
    } finally { setEnrolling(false); }
  };

  if (notFound && !course) {
    return (
      <div className="pt-32 text-center max-w-xl mx-auto px-6">
        <div className="overline mb-3">Kurs nicht gefunden</div>
        <h2 className="serif text-3xl font-light text-white/90 mb-4">Dieser Kurs existiert nicht</h2>
        <p className="text-white/60 leading-relaxed mb-8">
          Der Kurs „{slug}" ist entweder nicht mehr verfügbar oder du hast dich in der Adresse
          verschrieben. Kehre zur Übersicht zurück oder wähle ein anderes Angebot.
        </p>
        <Link to="/academy" className="btn-primary">Zur Kursübersicht</Link>
      </div>
    );
  }

  if (!course) return <div className="pt-32 text-center text-white/55">Lade…</div>;

  return (
    <div className="pt-24 min-h-screen" data-testid={`course-detail-${slug}`}>
      <div className="relative">
        {course.cover_image && (
          <div className="absolute inset-0 h-[60vh] z-0">
            <img src={course.cover_image} alt="" className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#081328] via-[#081328]/40 to-transparent"></div>
          </div>
        )}
        <div className="relative z-10 container-sat pt-20 pb-16">
          <Link to="/academy" className="btn-ghost text-sm">← Academy</Link>
          <div className="grid lg:grid-cols-12 gap-10 mt-10">
            <div className="lg:col-span-8">
              <div className="overline mb-3">{course.track === "mentor" ? "Mentor-Ausbildung" : course.track === "corporate" ? "Corporate" : "Academy"}</div>
              <h1 className="headline-xl text-5xl md:text-7xl">{course.title}</h1>
              <p className="serif italic text-2xl text-white/65 mt-4">{course.subtitle}</p>
              <p className="text-white/75 text-lg mt-8 leading-relaxed max-w-3xl">{course.description}</p>
            </div>
            <div className="lg:col-span-4">
              <div className="glass rounded-2xl p-6 sticky top-28" data-testid="course-buybox">
                <div className="text-3xl serif font-light text-[#D4AF37]">
                  {course.price_chf > 0 ? `CHF ${course.price_chf.toFixed(0)}` : "Kostenlos"}
                </div>
                <div className="text-xs text-white/45 mt-1">Einmalzahlung · lebenslanger Zugang</div>
                <button onClick={startEnroll} disabled={enrolling} className="btn-primary w-full justify-center mt-6" data-testid="course-enroll">
                  {enrolling ? <Loader2 className="animate-spin" size={18} /> : course.price_chf > 0 ? (<><ShoppingCart size={18} /> Jetzt buchen</>) : (<>Jetzt starten <ArrowRight size={18} /></>)}
                </button>
                {err && <p className="text-red-300 text-xs mt-2">{err}</p>}
                <ul className="mt-6 space-y-2 text-sm text-white/70">
                  <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> {course.modules?.length || 0} Module</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Module-Quizze + Zertifikat</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Forum-Zugang</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-[#D4AF37]" /> Lebenslanger Zugriff</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="py-20 bg-[#050B1B]">
        <div className="container-sat max-w-[1100px]">
          <div className="overline mb-3">Curriculum</div>
          <h2 className="headline-xl text-3xl md:text-5xl mb-10">{course.modules?.length || 0} Module · {course.modules?.reduce((a, m) => a + (m.lessons?.length || 0), 0) || 0} Lektionen</h2>
          <div className="space-y-3" data-testid="course-modules">
            {course.modules?.map((m, i) => (
              <details key={m.id} className="group glass rounded-xl overflow-hidden" data-testid={`module-${i}`}>
                <summary className="cursor-pointer p-5 flex items-center justify-between list-none">
                  <div className="flex items-center gap-4">
                    <span className="num-mark">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <div className="serif text-xl">{m.title}</div>
                      <div className="text-xs text-white/45 mt-1">{m.summary}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-white/50">
                    <span>{m.lessons?.length || 0} Lektionen</span>
                    {m.quiz?.length > 0 && <span className="px-2 py-1 rounded-full bg-[#D4AF37]/10 text-[#D4AF37]">Quiz</span>}
                  </div>
                </summary>
                <div className="px-5 pb-5">
                  <ul className="space-y-2 text-sm">
                    {m.lessons?.map((l, j) => (
                      <li key={l.id} className="flex items-center gap-3 text-white/70 py-2 border-t border-white/5">
                        <Lock size={12} className="text-white/30" />
                        <span className="flex-1">{l.title}</span>
                        <span className="text-xs text-white/40 flex items-center gap-1"><Clock size={11} /> {l.duration_min}m</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CourseDetail;
